import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';
import {
  computeMetric,
  getSameDayLastMonth,
  getSameDayLastWeek,
  toISTDateString,
  type Metric,
} from '@/lib/periods';
import type { MetricSource } from './useMetric';

const COLUMN: Record<MetricSource, string> = {
  net_sales: 'net_sales',
  orders: 'orders',
  covers: 'covers',
  avg_ticket: 'avg_ticket',
  aggregator_gmv: 'aggregator_gmv',
  aggregator_orders: 'aggregator_orders',
  aggregator_ads_spend: 'aggregator_ads_spend',
};

async function valueOnDate(
  outletId: string | 'all',
  column: string,
  date: Date,
): Promise<number> {
  let q = marketing
    .from('mv_outlet_kpi_daily')
    .select(column)
    .eq('date', toISTDateString(date));
  if (outletId !== 'all') q = q.eq('outlet_id', outletId);
  const { data, error } = await q;
  if (error) throw error;
  let total = 0;
  for (const row of (data ?? []) as unknown as Array<Record<string, unknown>>) {
    total += Number(row[column] ?? 0);
  }
  return total;
}

interface Result {
  yesterday: number;
  vsLastWeek: Metric;
  vsLastMonth: Metric;
  rangeYesterday: { start: Date; end: Date };
  rangeLastWeek: { start: Date; end: Date };
  rangeLastMonth: { start: Date; end: Date };
}

export function useDayMetric(outletId: string | 'all', source: MetricSource) {
  const lastWeek = getSameDayLastWeek();
  const lastMonth = getSameDayLastMonth();
  const column = COLUMN[source];

  return useQuery<Result>({
    queryKey: [
      'day-metric',
      source,
      outletId,
      lastWeek.current.start.toISOString(),
    ],
    queryFn: async () => {
      const [yest, week, month] = await Promise.all([
        valueOnDate(outletId, column, lastWeek.current.start),
        valueOnDate(outletId, column, lastWeek.previous.start),
        valueOnDate(outletId, column, lastMonth.previous.start),
      ]);
      return {
        yesterday: yest,
        vsLastWeek: computeMetric(yest, week),
        vsLastMonth: computeMetric(yest, month),
        rangeYesterday: { start: lastWeek.current.start, end: lastWeek.current.end },
        rangeLastWeek: { start: lastWeek.previous.start, end: lastWeek.previous.end },
        rangeLastMonth: { start: lastMonth.previous.start, end: lastMonth.previous.end },
      };
    },
    staleTime: 60_000,
  });
}
